import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  AccountBalance as BalanceIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const Finance = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAdmin } = useAuth();
  const [finances, setFinances] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [dealsStats, setDealsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFinance, setSelectedFinance] = useState(null);

  const [formData, setFormData] = useState({
    company: '',
    type: 'income',
    amount: '',
    description: '',
    status: 'pending',
    date: new Date(),
    invoiceNumber: '',
    paymentMethod: 'bank_transfer',
    manager: '',
  });
  const [managers, setManagers] = useState([]);

  const types = [
    { value: 'income', label: 'Доход' },
    { value: 'expense', label: 'Расход' },
    { value: 'invoice', label: 'Счет' },
    { value: 'payment', label: 'Платеж' },
    { value: 'deal', label: 'Сделка' },
  ];

  const statuses = [
    { value: 'pending', label: 'Ожидается' },
    { value: 'completed', label: 'Завершено' },
    { value: 'cancelled', label: 'Отменено' },
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Наличные' },
    { value: 'card', label: 'Карта' },
    { value: 'bank_transfer', label: 'Банковский перевод' },
    { value: 'other', label: 'Другое' },
  ];

  useEffect(() => {
    fetchData();
    fetchCompanies();
    fetchManagers();
  }, [isAdmin]);

  const fetchManagers = async () => {
    try {
      const response = await axios.get('/api/users/managers');
      setManagers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки менеджеров:', error);
    }
  };

  const fetchData = async () => {
    try {
      const requests = [
        axios.get('/api/finance'),
        axios.get('/api/finance/stats'),
      ];
      
      // Для админа загружаем статистику по сделкам
      if (isAdmin) {
        requests.push(axios.get('/api/finance/deals-stats'));
      }
      
      const responses = await Promise.all(requests);
      setFinances(responses[0].data);
      setStats(responses[1].data);
      
      if (isAdmin && responses[2]) {
        setDealsStats(responses[2].data);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Ошибка загрузки компаний:', error);
    }
  };

  const handleOpenDialog = (finance = null) => {
    if (finance) {
      setSelectedFinance(finance);
      setFormData({
        company: finance.company._id,
        type: finance.type,
        amount: finance.amount,
        description: finance.description,
        status: finance.status,
        date: new Date(finance.date),
        invoiceNumber: finance.invoiceNumber || '',
        paymentMethod: finance.paymentMethod,
        manager: finance.manager?._id || finance.manager || '',
      });
    } else {
      setSelectedFinance(null);
      setFormData({
        company: '',
        type: 'income',
        amount: '',
        description: '',
        status: 'pending',
        date: new Date(),
        invoiceNumber: '',
        paymentMethod: 'bank_transfer',
        manager: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFinance(null);
  };

  const handleSubmit = async () => {
    // Валидация
    if (!formData.company) {
      alert('Пожалуйста, выберите компанию');
      return;
    }
    if (!formData.amount || isNaN(Number(formData.amount))) {
      alert('Пожалуйста, введите корректную сумму');
      return;
    }
    if (!formData.description || formData.description.trim() === '') {
      alert('Пожалуйста, введите описание');
      return;
    }
    
    try {
      const dataToSend = {
        ...formData,
        amount: Number(formData.amount),
      };
      
      if (selectedFinance) {
        await axios.patch(`/api/finance/${selectedFinance._id}`, dataToSend);
      } else {
        await axios.post('/api/finance', dataToSend);
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      const message = error.response?.data?.message || 'Ошибка сохранения записи';
      alert(message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись?')) {
      try {
        await axios.delete(`/api/finance/${id}`);
        fetchData();
      } catch (error) {
        console.error('Ошибка удаления:', error);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(amount);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'income': return '#a3be8c';
      case 'expense': return '#bf616a';
      case 'invoice': return '#88c0d0';
      case 'payment': return '#5e81ac';
      case 'deal': return '#b48ead';
      default: return '#8b949e';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#a3be8c';
      case 'pending': return '#ebcb8b';
      case 'cancelled': return '#bf616a';
      default: return '#8b949e';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка финансов...
          </Typography>
        </Box>
      </Layout>
    );
  }

  const StatCard = ({ title, value, icon, color, delay = 0 }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        animation: `fadeInUp 0.5s ease ${delay}s both`,
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2" fontWeight={500}>
              {title}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800,
                background: `linear-gradient(135deg, ${color} 0%, ${color}aa 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {formatCurrency(value)}
            </Typography>
          </Box>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}aa 100%)`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 20px ${color}40`,
            }}
          >
            {React.cloneElement(icon, { sx: { color: '#0d1117', fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Layout>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              background: isDarkMode 
                ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
                : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            💰 Финансы
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Добавить запись
          </Button>
        </Box>

        {/* Статистика */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Доходы"
              value={stats?.totalIncome || 0}
              icon={<IncomeIcon />}
              color="#a3be8c"
              delay={0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Расходы"
              value={stats?.totalExpense || 0}
              icon={<ExpenseIcon />}
              color="#bf616a"
              delay={0.1}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Баланс"
              value={stats?.balance || 0}
              icon={<BalanceIcon />}
              color="#88c0d0"
              delay={0.2}
            />
          </Grid>
        </Grid>

        {/* Статистика по сделкам (только для админа) */}
        {isAdmin && dealsStats && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 Статистика по сделкам менеджеров
              </Typography>
              
              {/* Общая статистика по периодам */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      background: isDarkMode ? 'rgba(163, 190, 140, 0.1)' : 'rgba(163, 190, 140, 0.08)',
                      border: '1px solid rgba(163, 190, 140, 0.3)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">Сегодня</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#a3be8c' }}>
                      {formatCurrency(dealsStats.totals?.today || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dealsStats.totals?.todayCount || 0} сделок
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      background: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'rgba(136, 192, 208, 0.08)',
                      border: '1px solid rgba(136, 192, 208, 0.3)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">Неделя</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#88c0d0' }}>
                      {formatCurrency(dealsStats.totals?.week || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dealsStats.totals?.weekCount || 0} сделок
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      background: isDarkMode ? 'rgba(180, 142, 173, 0.1)' : 'rgba(180, 142, 173, 0.08)',
                      border: '1px solid rgba(180, 142, 173, 0.3)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">Месяц</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#b48ead' }}>
                      {formatCurrency(dealsStats.totals?.month || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dealsStats.totals?.monthCount || 0} сделок
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      background: isDarkMode ? 'rgba(94, 129, 172, 0.1)' : 'rgba(94, 129, 172, 0.08)',
                      border: '1px solid rgba(94, 129, 172, 0.3)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">Год</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#5e81ac' }}>
                      {formatCurrency(dealsStats.totals?.year || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dealsStats.totals?.yearCount || 0} сделок
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Таблица по менеджерам */}
              {dealsStats.byManager && dealsStats.byManager.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Менеджер</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Сегодня</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Неделя</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Месяц</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Год</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Всего сделок</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Общая сумма</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dealsStats.byManager.map((manager, index) => (
                        <TableRow 
                          key={manager.managerId || index}
                          sx={{
                            '&:hover': { 
                              background: isDarkMode ? 'rgba(136, 192, 208, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>
                            {manager.managerName?.trim() || 'Неизвестный'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#a3be8c' }}>
                            {formatCurrency(manager.todayAmount || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#88c0d0' }}>
                            {formatCurrency(manager.weekAmount || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#b48ead' }}>
                            {formatCurrency(manager.monthAmount || 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#5e81ac' }}>
                            {formatCurrency(manager.yearAmount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={manager.totalDeals} 
                              size="small"
                              sx={{ 
                                background: 'rgba(136, 192, 208, 0.2)',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCurrency(manager.totalAmount || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {(!dealsStats.byManager || dealsStats.byManager.length === 0) && (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  Нет данных по сделкам
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Таблица финансов */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Дата</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Компания</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Тип</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Описание</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Сумма</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Статус</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {finances.map((finance, index) => (
                  <TableRow 
                    key={finance._id} 
                    sx={{
                      '&:hover': { 
                        background: isDarkMode ? 'rgba(136, 192, 208, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      },
                      animation: `fadeIn 0.3s ease ${index * 0.03}s both`,
                      '@keyframes fadeIn': {
                        '0%': { opacity: 0 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  >
                    <TableCell>
                      {new Date(finance.date).toLocaleDateString('ru-RU')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{finance.company?.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={types.find(t => t.value === finance.type)?.label}
                        size="small"
                        sx={{
                          background: `${getTypeColor(finance.type)}30`,
                          color: getTypeColor(finance.type),
                          border: `1px solid ${getTypeColor(finance.type)}`,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{finance.description}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 700,
                        color: finance.type === 'income' ? '#a3be8c' : finance.type === 'expense' ? '#bf616a' : 'text.primary',
                      }}
                    >
                      {finance.type === 'expense' ? '-' : ''}{formatCurrency(finance.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statuses.find(s => s.value === finance.status)?.label}
                        size="small"
                        sx={{
                          background: `${getStatusColor(finance.status)}30`,
                          color: getStatusColor(finance.status),
                          border: `1px solid ${getStatusColor(finance.status)}`,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(finance)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {isAdmin && (
                        <IconButton size="small" onClick={() => handleDelete(finance._id)} sx={{ color: '#bf616a' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Диалог создания/редактирования */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {selectedFinance ? 'Редактировать запись' : 'Новая финансовая запись'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              select
              label="Компания"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              margin="normal"
              required
            >
              {companies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Тип операции"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              margin="normal"
            >
              {types.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Сумма"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Описание"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              required
            />

            <DatePicker
              label="Дата"
              value={formData.date}
              onChange={(date) => setFormData({ ...formData, date })}
              sx={{ width: '100%', mt: 2 }}
            />

            <TextField
              fullWidth
              select
              label="Статус"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              margin="normal"
            >
              {statuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Способ оплаты"
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
              margin="normal"
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Номер счета (опционально)"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              select
              label="Менеджер (кто заработал)"
              value={formData.manager}
              onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
              margin="normal"
              helperText="Выберите менеджера, который заработал эту сумму"
            >
              <MenuItem value="">
                <em>Не указан</em>
              </MenuItem>
              {managers.map((manager) => (
                <MenuItem key={manager._id} value={manager._id}>
                  {manager.firstName} {manager.lastName}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} variant="outlined">Отмена</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedFinance ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogActions>
        </Dialog>
      </Layout>
    </LocalizationProvider>
  );
};

export default Finance;
